import { Dialog } from '@headlessui/react'
import { useCallback, useEffect, useState } from 'preact/hooks'
import { useLocalStorage } from 'react-use'
import { useCurrentRole } from '../hooks'
import { TextButton } from '../text-button/text-button'
import { TabletModeCheckbox, type useTabletMode } from './toggle-tablet-mode'

export type DispatchTransaction = {
  productId: string
  inputPackages: number
  inputUnits: number
}

export function useDispatchFormState() {
  const [persistedState, setPersistedState] = useLocalStorage(
    'dispatch-form-state',
    {
      sourceId: '',
      targetId: '',
      comment: '',
      transactions: {}
    }
  )

  const [sourceId, setSourceId] = useState(persistedState?.sourceId ?? '')
  const [targetId, setTargetId] = useState(persistedState?.targetId ?? '')
  const [comment, setComment] = useState(persistedState?.comment ?? '')
  const [transactions, setTransactions] = useState<
    Record<string, DispatchTransaction>
  >(persistedState?.transactions ?? {})

  const handleUpdateTransactions = useCallback(
    (update: DispatchTransaction) => {
      setTransactions((prev) => {
        const newTransactions = { ...prev }

        if (update.inputPackages === 0 && update.inputUnits === 0) {
          delete newTransactions[update.productId]
        } else {
          newTransactions[update.productId] = update
        }

        return newTransactions
      })
    },
    []
  )

  const resetFormState = useCallback(() => {
    setSourceId('')
    setTargetId('')
    setComment('')
    setTransactions({})
  }, [])

  const clearPersistedState = useCallback(() => {
    setPersistedState({
      sourceId: '',
      targetId: '',
      comment: '',
      transactions: {}
    })
  }, [setPersistedState])

  useEffect(() => {
    setPersistedState({
      sourceId,
      targetId,
      comment,
      transactions
    })
  }, [sourceId, targetId, comment, transactions, setPersistedState])

  return {
    sourceId,
    setSourceId,
    targetId,
    setTargetId,
    comment,
    setComment,
    transactions,
    handleUpdateTransactions,
    resetFormState,
    clearPersistedState
  }
}

export function DispatchFormTitle({
  targetName,
  isTabletMode,
  setIsTabletMode,
  onClose
}: {
  targetName?: string
  isTabletMode: boolean
  setIsTabletMode: ReturnType<typeof useTabletMode>[1]
  onClose: () => void
}) {
  const role = useCurrentRole()

  return (
    <fieldset style={{ paddingBottom: 0 }}>
      <Dialog.Title
        as='legend'
        style={{
          display: 'grid',
          gridTemplateColumns: 'auto max-content min-content',
          alignItems: 'center',
          gap: '1em'
        }}
      >
        <strong>
          {role === 'bar'
            ? `Produkte anfordern für ${targetName}`
            : 'Produkte dispatchen'}
        </strong>

        <TabletModeCheckbox checked={isTabletMode} onChange={setIsTabletMode} />
        <TextButton onClick={onClose}>Schließen</TextButton>
      </Dialog.Title>
    </fieldset>
  )
}
